/**
 * Data validation and sanitization utilities for API responses
 * Ensures all data has safe fallbacks and prevents runtime errors
 */

import { InsightsData, Insight, Metrics, SuggestedAction } from '../types/insights';
import { safeString, safeNumber, safeArray } from './safeString';

/**
 * Validates and sanitizes a SuggestedAction object
 */
export const validateSuggestedAction = (action: any): SuggestedAction => {
  return {
    action: safeString(action?.action, 'No action specified'),
    action_type: safeString(action?.action_type, 'General'),
    system_prompt: safeString(action?.system_prompt, ''),
    first_assistant_message: safeString(action?.first_assistant_message, ''),
  };
};

/**
 * Validates and sanitizes an Insight object
 */
export const validateInsight = (insight: any): Insight => {
  const rawMode = safeString(insight?.insight_mode);
  const insightMode = rawMode ? (rawMode as Insight['insight_mode']) : undefined;

  return {
    category: safeString(insight?.category, 'general'),
    category_icon: safeString(insight?.category_icon, '📊'),
    priority_tag: safeString(insight?.priority_tag ?? insight?.priority, 'opportunity'),
    insight_mode: insightMode,
    main_insight: safeString(insight?.main_insight, 'No insight available'),
    why_it_matters: safeString(insight?.why_it_matters, 'Impact not specified'),
    recommendation: safeString(insight?.recommendation, 'No recommendation available'),
    suggested_actions: safeArray(insight?.suggested_actions).map(validateSuggestedAction),
    follow_up_questions: safeArray<string>(insight?.follow_up_questions)
      .map((question) => safeString(question, ''))
      .filter(Boolean),
    data_handles: safeArray<string>(insight?.data_handles)
      .map((handle) => safeString(handle, ''))
      .filter(Boolean),
  };
};

/**
 * Validates and sanitizes a Metrics object
 */
export const validateMetrics = (metrics: any): Metrics => {
  return {
    go2_score: safeNumber(metrics?.go2_score, 0),
    go2_score_trend: safeString(metrics?.go2_score_trend),
    engagement_score: safeNumber(metrics?.engagement_score, 0),
    engagement_trend: safeString(metrics?.engagement_trend),
  };
};

/**
 * Validates and sanitizes an InsightsData object from API response
 */
export const validateInsightsData = (data: any): InsightsData => {
  return {
    date: safeString(data?.date, new Date().toISOString().split('T')[0]),
    user_name: safeString(data?.user_name, 'Unknown User'),
    metrics: validateMetrics(data?.metrics),
    insights: safeArray(data?.insights).map(validateInsight),
  };
};

/**
 * Type guard to check if data looks like valid InsightsData
 */
export const isValidInsightsResponse = (data: any): boolean => {
  return (
    data &&
    typeof data === 'object' &&
    (data.insights || data.metrics || data.user_name || data.date)
  );
};

/**
 * Safe API response handler with validation
 */
export const safeApiResponse = <T>(
  data: any,
  validator: (data: any) => T,
  fallback: T
): T => {
  try {
    if (!data) return fallback;
    return validator(data);
  } catch (error) {
    console.warn('Data validation failed, using fallback:', error);
    return fallback;
  }
};
